/**
 * Reference catalogs slice — global, session-scoped lookup tables for
 * relay load types, light fixtures, alarm definitions, alarm categories,
 * customer types, SmartLink unit models, and firmware versions.
 *
 * Fetched once per session via GET /api/odl-app/reference/ and FE-cached.
 * All dashboard *_id fields resolve against these catalogs at render time.
 * No socket updates — catalogs are static; refresh on app reload.
 *
 * Contains:
 * - reference slice
 * - selectRelayLoadTypes / selectLightFixtures / selectAlarmDefinitions /
 *   selectAlarmCategories / selectCustomerTypes / selectUnitModels /
 *   selectFirmwareVersions / selectReferenceStatus selectors
 * - selectCustomerTypeById — parametrized (memoizeByKey) customer-type lookup
 *   backing useRelayLoadTerm's per-customer term resolution
 * - selectFirmwareVersionById — parametrized (memoizeByKey) firmware-version
 *   lookup backing the site quick actions declared status-command join
 * - selectAvailableLoadTypeIdsForCustomerType / selectDefaultLoadTypeIdForCustomerType —
 *   parametrized (memoizeByKey) load-type availability/default lookups backing
 *   the Panel Type dropdown's customer-type scoping
 * - selectRelayLoadTypeCapsById — the RelayLoadType capability-flag lookup
 *   (is_digital, supports_light_fixtures, supports_normally_closed,
 *   supports_reboot_command, is_triface_motor, supports_pairing) backing every
 *   load-type KIND check that used to hardcode a catalog id. Map-building and
 *   the getLoadTypeCapabilities(capsById, id) lookup live in the pure
 *   loadTypeCapabilities.js sibling so store-free consumers (relayConfigForm.js)
 *   can use them without pulling in this slice's axios/thunk chain.
 * - selectLightFixturesAsImmutableMap / selectRelayLoadTypesAsImmutableMap /
 *   selectUnitModelsAsImmutableMap — Immutable.js Map keyed by id string
 *   for billboard/legacy consumers that use .get() / .toList() APIs
 */
import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
// eslint-disable-next-line no-restricted-imports -- approved output-boundary shim for billboard consumers
import { Map as ImmutableMap } from 'immutable';

import { fetchReference } from '@sophocles/api/siteDashboard';
import { extractThunkError } from '@sophocles/api/thunkUtils';
import { buildLoadTypeCapsById } from '@sophocles/features/reference/loadTypeCapabilities';
import { memoizeByKey } from '@sophocles/utils/memoizeByKey';

export const loadReference = createAsyncThunk(
  'reference/load',
  async (_arg, { rejectWithValue }) => {
    try {
      return await fetchReference();
    } catch (e) {
      return rejectWithValue(extractThunkError(e));
    }
  },
  {
    // Only fetch once per session — skip if already loaded successfully.
    condition: (_arg, { getState }) => {
      const { reference } = getState();
      return !reference.loaded && !reference.loading;
    },
  },
);

const slice = createSlice({
  name: 'reference',
  initialState: {
    loaded: false,
    loading: false,
    error: null,
    /** @type {object[]} RelayLoadType catalog */
    relayLoadTypes: [],
    /** @type {object[]} LightFixture catalog */
    lightFixtures: [],
    /** @type {object[]} Alarm definitions (filtered by requester on backend) */
    alarmDefinitions: [],
    /** @type {object[]} AlarmCategory 2-level tree */
    alarmCategories: [],
    /** @type {object[]} CustomerType catalog (carries relay_load_term / _plural) */
    customerTypes: [],
    /** @type {object[]} SmartLink unit-model catalog (non-obsolete only) */
    unitModels: [],
    /** @type {object[]} SmartLink firmware-version catalog (declared default_status_command) */
    firmwareVersions: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadReference.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadReference.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.relayLoadTypes = action.payload.relay_load_types ?? [];
        state.lightFixtures = action.payload.light_fixtures ?? [];
        state.alarmDefinitions = action.payload.alarms ?? [];
        state.alarmCategories = action.payload.alarm_categories ?? [];
        state.customerTypes = action.payload.customer_types ?? [];
        state.unitModels = action.payload.models ?? [];
        state.firmwareVersions = action.payload.firmware_versions ?? [];
      })
      .addCase(loadReference.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to load reference data';
      });
  },
});

// ── Selectors ────────────────────────────────────────────────────────────────

export const selectRelayLoadTypes = state => state.reference.relayLoadTypes;
export const selectLightFixtures = state => state.reference.lightFixtures;
export const selectAlarmDefinitions = state => state.reference.alarmDefinitions;
export const selectUnitModels = state => state.reference.unitModels;
export const selectAlarmCategories = state => state.reference.alarmCategories;
export const selectCustomerTypes = state => state.reference.customerTypes;
export const selectFirmwareVersions = state => state.reference.firmwareVersions;
export const selectReferenceStatus = state => ({
  loaded: state.reference.loaded,
  loading: state.reference.loading,
  error: state.reference.error,
});

/**
 * Parametrized selector: the CustomerType catalog row for a given id (ref id or
 * bare id), or null when absent. Parametrized → wrapped in memoizeByKey so the
 * built selector identity is stable per id across renders (useSelector
 * referential-stability contract). Backs useRelayLoadTerm.
 */
export const selectCustomerTypeById = memoizeByKey(customerTypeId =>
  createSelector(
    selectCustomerTypes,
    customerTypes => customerTypes.find(type => String(type.id) === String(customerTypeId)) ?? null,
  ),
);

/**
 * Parametrized selector: the FirmwareVersion catalog row for a given id, or
 * null when absent. Parametrized → wrapped in memoizeByKey so the built
 * selector identity is stable per id across renders. Backs the site quick
 * actions declared-status-command join (unit.firmware_version.id → row).
 */
export const selectFirmwareVersionById = memoizeByKey(firmwareVersionId =>
  createSelector(
    selectFirmwareVersions,
    firmwareVersions => firmwareVersions.find(fw => String(fw.id) === String(firmwareVersionId)) ?? null,
  ),
);

/**
 * Parametrized selector: the set of RelayLoadType ids available to a given
 * CustomerType, denormalized from the `available_load_types` entity-ref
 * array (`{type, id, name}`) the reference contract emits on each
 * `customer_types` row. Empty array when the type has no configured set
 * (callers fall back to the full catalog — see filterRelayLoadTypeOptions).
 */
export const selectAvailableLoadTypeIdsForCustomerType = memoizeByKey(customerTypeId =>
  createSelector(selectCustomerTypeById(customerTypeId), customerType =>
    (customerType?.available_load_types ?? []).map(ref => ref.id),
  ),
);

/**
 * Parametrized selector: the default RelayLoadType id to preselect for a new
 * relay/load under a given CustomerType, denormalized from the
 * `default_load_type` entity ref, or null when unset.
 */
export const selectDefaultLoadTypeIdForCustomerType = memoizeByKey(customerTypeId =>
  createSelector(selectCustomerTypeById(customerTypeId), customerType => customerType?.default_load_type?.id ?? null),
);

/**
 * The RelayLoadType catalog's capability flags, keyed by id. Each row is
 * safe-defaulted per-flag so a partial catalog row still yields booleans
 * (see loadTypeCapabilities.js). Consumed directly by pure domain modules
 * (e.g. relayConfigForm.js) that iterate many relays and can't call a
 * parametrized redux selector per relay; components needing a single id's
 * flags call `getLoadTypeCapabilities` (loadTypeCapabilities.js) against
 * this map.
 */
export const selectRelayLoadTypeCapsById = createSelector(selectRelayLoadTypes, buildLoadTypeCapsById);

/**
 * Returns the light fixture catalog as an Immutable Map keyed by id string.
 * For legacy billboard consumers that rely on .get(id) / .toList().toJS() APIs.
 */
// DEPRECATED(immutable-freeze-shims): billboard-only shim; drop once features/reference/index.js, views/sites/MeasurementDataView.jsx, and views/sites/PowerDataView.jsx stop consuming the Immutable shape.
export const selectLightFixturesAsImmutableMap = createSelector(selectLightFixtures, fixtures =>
  fixtures.reduce((acc, f) => acc.set(String(f.id), ImmutableMap(f)), ImmutableMap()),
);

/**
 * Returns the RelayLoadType catalog as an Immutable Map keyed by id string.
 * For legacy billboard consumers that rely on .get(id, Map()) APIs.
 */
// DEPRECATED(immutable-freeze-shims): billboard-only shim; drop once entities/Relay/relaySelectors.js stops consuming the Immutable shape.
export const selectRelayLoadTypesAsImmutableMap = createSelector(selectRelayLoadTypes, types =>
  types.reduce((acc, type) => acc.set(String(type.id), ImmutableMap(type)), ImmutableMap()),
);

/**
 * Returns the SmartLink unit-model catalog as an Immutable Map keyed by id string.
 * For legacy billboard consumers that rely on .get(id, null) APIs.
 */
// DEPRECATED(immutable-freeze-shims): billboard-only shim; drop once features/sites/slices/selectedPlant/selectors.js and features/sites/slices/selectedStructure/selectors.js stop consuming the Immutable shape.
export const selectUnitModelsAsImmutableMap = createSelector(selectUnitModels, models =>
  models.reduce((acc, model) => acc.set(String(model.id), ImmutableMap(model)), ImmutableMap()),
);

const { reducer } = slice;
export default reducer;
